<div class="skills-panel" [ngSwitch]="view">

    <!-- ============================================================ -->
    <!-- LIST                                                          -->
    <!-- ============================================================ -->
    <div class="skills-list" *ngSwitchCase="'list'">
        <!-- Search and "New skill" are both dead controls with nothing to
             search and nothing to sit alongside — the empty state below carries
             the same three actions as this toolbar's menu.
             `!loading` as well, and the same on every block below: `panelEmpty`
             is false WHILE the list is in flight, so a panel opened cold used to
             assemble its toolbar, drafts and footnote around a spinner and then
             throw all three away the moment the response said "no skills". One
             load state, then one final state. -->
        <div class="skills-toolbar" *ngIf="!loading && !panelEmpty && !listLoadFailed">
            <div class="search-field">
                <i class="ph-bold ph-magnifying-glass icon-muted icon-16" aria-hidden="true"></i>
                <input
                    type="text"
                    placeholder="Search skills"
                    [(ngModel)]="searchTerm"
                    aria-label="Search skills">
            </div>
            <button type="button" class="btn-pill" [matMenuTriggerFor]="addMenu">
                <i class="ph-bold ph-plus icon-16" aria-hidden="true"></i>
                <span>New skill</span>
            </button>
            <mat-menu #addMenu="matMenu" xPosition="before" class="gable-menu-panel">
                <button mat-menu-item (click)="onAddUpload()">
                    <i class="ph-bold ph-upload-simple icon-muted icon-16" aria-hidden="true"></i>
                    <span>Upload a file</span>
                </button>
                <button mat-menu-item (click)="onAddWriteWithGable()">
                    <i class="ph-bold ph-sparkle icon-muted icon-16" aria-hidden="true"></i>
                    <span>Write it with Gable</span>
                </button>
                <button mat-menu-item (click)="onAddFromLibrary()">
                    <i class="ph-bold ph-books icon-muted icon-16" aria-hidden="true"></i>
                    <span>Browse the PayHOA library</span>
                </button>
            </mat-menu>
        </div>

        <!-- `listLoaded`, a one-shot first-load flag, NOT the per-request
             `loading`: approving a proposed skill calls `loadList()`, so gating on
             `loading` unmounted this child mid-review — discarding the live
             FormGroup of every OTHER draft card, refetching the drafts, and
             collapsing the section as a layout jump. Same pattern as the activity
             panel's `feedLoaded`. -->
        <app-gable-draft-review class="skills-proposed"
                                *ngIf="listLoaded"
                                kind="skill"
                                (countChanged)="onProposedCountChanged($event)"></app-gable-draft-review>

        <!-- Skeleton on FIRST load only (`!listLoaded`), and the plain spinner
             on every refetch. `loading` alone would blank the whole grid when
             approving a proposed skill refires `loadList()` — the same unmount
             the draft-review comment above documents. -->
        <div class="skills-skeleton" *ngIf="!listLoaded && !listLoadFailed" role="status" aria-live="polite" aria-busy="true">
            <span class="visually-hidden">Loading skills</span>
            <span class="gable-skeleton-line gable-skeleton-line--xs skills-skeleton-count"></span>
            <div class="skills-grid">
                <div class="skill-card skills-skeleton-card" *ngFor="let card of skeletonCards4; trackBy: trackByIndex" aria-hidden="true">
                    <span class="skills-skeleton-icon"></span>
                    <span class="skills-skeleton-body">
                        <span class="gable-skeleton-line gable-skeleton-line--md"></span>
                        <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                        <span class="gable-skeleton-line gable-skeleton-line--sm"></span>
                    </span>
                </div>
            </div>
        </div>

        <div class="skills-loading" *ngIf="loading && listLoaded" aria-busy="true">
            <i class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin" aria-hidden="true"></i>
            <span>Loading skills…</span>
        </div>

        <div class="skills-cap-warning" *ngIf="!loading && skills.length >= 40">
            <app-alert-block type="warn" [filled]="true">
                {{ skills.length }} of 50 skills. Gable offers at most 50 per conversation (most-used first) —
                past the cap, the least-used stop being listed. Retire stale skills to stay under.
            </app-alert-block>
        </div>

        <!-- `!panelEmpty`, not just `!loading`: with no skills at all the list
             has nothing to say, and the block below owns that case.
             `!listLoadFailed` as well, and on the toolbar and helper too: a
             failed load leaves `skills` empty with `panelEmpty` false (it is
             deliberately false on failure), so all three used to assemble
             around nothing and the search-empty branch told a user whose
             request 403'd that no skills matched their search. One failure,
             one message — the error block below. -->
        <ng-container *ngIf="!loading && !panelEmpty && !listLoadFailed">
            <ng-container *ngIf="filteredSkills.length > 0; else emptyState">
                <div class="skills-count-row">All skills ({{ filteredSkills.length }})</div>
                <div class="skills-grid">
                    <button
                        type="button"
                        class="skill-card"
                        *ngFor="let s of filteredSkills; trackBy: trackBySkillId"
                        [class.is-off]="!s.enabled"
                        (click)="openDetail(s)">
                        <span class="skill-card-icon" aria-hidden="true">
                            <i class="ph-bold icon-18" [ngClass]="skillIcon(s)"></i>
                        </span>
                        <span class="skill-card-body">
                            <span class="skill-card-name-row">
                                <span class="skill-card-name">{{ s.name }}</span>
                                <span class="off-tag" *ngIf="!s.enabled">Off</span>
                            </span>
                            <span class="skill-card-desc">{{ s.description || 'No description yet.' }}</span>
                            <span class="skill-card-footer" *ngIf="s.updatedAt">Updated {{ relativeUpdated(s.updatedAt) }}</span>
                        </span>
                    </button>
                </div>
            </ng-container>
            <!-- Only reachable with skills behind the search, so this is a
                 "no match", not a "none yet" — those are different problems and
                 the first one is fixed by clearing the box. -->
            <ng-template #emptyState>
                <p class="skills-no-match">No skills match your search.</p>
            </ng-template>
        </ng-container>

        <p class="skills-helper" *ngIf="!loading && !panelEmpty && !listLoadFailed">
            Gable reads a skill's description and pulls it in when the work matches. Skills change how it
            writes and decides — they never let it act without your approval.
        </p>

        <!-- A failed load is not an empty list: without this the block below told
             a user with forty skills that they had none, and offered to create
             one. Mirrors the Instructions panel's retry. -->
        <p class="skills-load-error" *ngIf="listLoadFailed && !loading" role="alert">
            Couldn't load your skills.
            <button type="button" class="skills-retry" (click)="loadList()">Try again</button>
        </p>

        <!-- The whole empty state, once: what a skill is, and the three ways to
             make one. The explanation used to be split across a splash header, a
             "No skills yet" card and a footnote, none of which was actionable. -->
        <div class="skills-empty" *ngIf="panelEmpty">
            <span class="skills-empty-icon" aria-hidden="true">
                <i class="ph-bold ph-scroll icon-20" aria-hidden="true"></i>
            </span>
            <h3 class="skills-empty-title">No skills yet</h3>
            <p class="skills-empty-copy">
                A skill is a standing instruction Gable pulls in when the work matches it — house style,
                a policy to quote, a checklist to follow. Skills change how Gable writes and decides,
                never what it can do without your approval.
            </p>
            <div class="skills-empty-actions">
                <!-- The primary route of the three, so it carries the filled
                     primary-blue pill; the sparkle inherits white from it. -->
                <button type="button" class="btn-pill" (click)="onAddWriteWithGable()">
                    <i class="ph-bold ph-sparkle icon-16" aria-hidden="true"></i>
                    <span>Write one with Gable</span>
                </button>
                <button type="button" class="btn-sm btn-secondary" (click)="onAddFromLibrary()">
                    <i class="ph-bold ph-books icon-16" aria-hidden="true"></i>
                    <span>Browse the library</span>
                </button>
                <button type="button" class="btn-sm btn-secondary" (click)="onAddUpload()">
                    <i class="ph-bold ph-upload-simple icon-16" aria-hidden="true"></i>
                    <span>Upload a file</span>
                </button>
            </div>
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- DETAIL                                                        -->
    <!-- ============================================================ -->
    <div class="skill-detail" *ngSwitchCase="'detail'">
        <button type="button" class="back-link" (click)="backToList()">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>All skills</span>
        </button>

        <div class="detail-skeleton" *ngIf="detailLoading" role="status" aria-live="polite" aria-busy="true">
            <span class="visually-hidden">Loading skill</span>
            <span class="gable-skeleton-line gable-skeleton-line--sm detail-skeleton-title" aria-hidden="true"></span>
            <span class="gable-skeleton-line gable-skeleton-line--xs" aria-hidden="true"></span>
            <div class="detail-skeleton-body" aria-hidden="true">
                <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                <span class="gable-skeleton-line gable-skeleton-line--md"></span>
            </div>
        </div>

        <ng-container *ngIf="!detailLoading && selected as skill">
            <div class="detail-header">
                <div class="detail-title-col">
                    <h3 class="detail-name">{{ skill.name }}</h3>
                    <p class="detail-meta">
                        by {{ skill.authorName }} · updated {{ shortDate(skill.updatedAt) }}
                        <span *ngIf="skill.isEdited"> · Edited</span>
                    </p>
                </div>
                <div class="detail-actions">
                    <label class="toggle-switch">
                        <input type="checkbox" [checked]="skill.enabled" (change)="toggleEnabled()" aria-label="Enable skill">
                        <span class="toggle-track"><span class="toggle-thumb"></span></span>
                    </label>
                    <button type="button" class="icon-btn" (click)="openEdit()" aria-label="Edit skill">
                        <i class="ph-bold ph-pencil-simple icon-16" aria-hidden="true"></i>
                    </button>
                    <button type="button" class="icon-btn" [matMenuTriggerFor]="kebabMenu" aria-label="Skill options">
                        <i class="ph-bold ph-dots-three icon-16" aria-hidden="true"></i>
                    </button>
                    <mat-menu #kebabMenu="matMenu" xPosition="before" class="gable-menu-panel">
                        <button mat-menu-item (click)="tryInChat()">
                            <i class="ph-bold ph-chat-circle icon-muted icon-16" aria-hidden="true"></i>
                            <span>Try it in chat</span>
                        </button>
                        <button mat-menu-item (click)="editWithGable()">
                            <i class="ph-bold ph-sparkle icon-muted icon-16" aria-hidden="true"></i>
                            <span>Edit with Gable</span>
                        </button>
                        <button mat-menu-item (click)="triggerReplaceFile()">
                            <i class="ph-bold ph-upload-simple icon-muted icon-16" aria-hidden="true"></i>
                            <span>Replace file</span>
                        </button>
                        <button mat-menu-item (click)="downloadSkill()">
                            <i class="ph-bold ph-download-simple icon-muted icon-16" aria-hidden="true"></i>
                            <span>Download</span>
                        </button>
                        <button mat-menu-item class="menu-item-danger" (click)="confirmRemove()">
                            <i class="ph-bold ph-trash icon-16" aria-hidden="true"></i>
                            <span>Remove</span>
                        </button>
                    </mat-menu>
                </div>
            </div>

            <div class="instructions-card" *ngIf="skill.body">
                <div class="markdown-body" [innerHTML]="playbookBody(skill.body) | markdown: 'final'"></div>
            </div>

            <div class="no-description-callout" *ngIf="!skill.description">
                <i class="ph-bold ph-info icon-16" aria-hidden="true"></i>
                <span>This skill has no description, so Gable can't match on it.</span>
            </div>

            <div class="applies-to-row">
                <div>
                    <span class="applies-label">Applies to</span>
                    <span class="applies-value">{{ skill.reachSummary }}</span>
                </div>
                <button type="button" class="link-btn" (click)="openReachEditor('detail')">Change</button>
            </div>
        </ng-container>
    </div>

    <!-- ============================================================ -->
    <!-- EDIT                                                          -->
    <!-- ============================================================ -->
    <div class="skill-edit" *ngSwitchCase="'edit'">
        <button type="button" class="back-link" (click)="cancelEdit()">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>Cancel</span>
        </button>

        <!-- role=status: the banner reports what was parsed out of the dropped
             file, and it appears on a view switch a screen reader is otherwise
             given no signal about. -->
        <div class="edit-banner" *ngIf="edit.banner" role="status">
            <i class="ph-bold ph-info icon-16" aria-hidden="true"></i>
            <span>{{ edit.banner }}</span>
        </div>

        <label class="field-label" for="skillNameInput">Skill name</label>
        <input
            id="skillNameInput"
            type="text"
            class="text-field"
            [(ngModel)]="edit.name"
            maxlength="100"
            placeholder="delinquency-escalation-ladder">
        <p class="file-name-preview">{{ fileNamePreview }}</p>

        <label class="field-label" for="skillDescInput">Description</label>
        <textarea
            id="skillDescInput"
            class="text-field"
            rows="2"
            maxlength="1000"
            [(ngModel)]="edit.description"
            placeholder="One sentence Gable can match this skill on."></textarea>

        <label class="field-label" for="skillBodyInput">Instructions</label>
        <textarea
            id="skillBodyInput"
            class="text-field body-field"
            rows="14"
            maxlength="65000"
            spellcheck="false"
            [(ngModel)]="edit.body"></textarea>

        <div class="applies-to-row">
            <div>
                <span class="applies-label">Applies to</span>
                <span class="applies-value">{{ editReachSummaryText }}</span>
            </div>
            <button type="button" class="link-btn" (click)="openReachEditor('edit')">Change</button>
        </div>

        <div class="edit-actions">
            <button type="button" class="btn-sm btn-secondary" (click)="cancelEdit()">Cancel</button>
            <button type="button" class="btn-sm btn-primary" [disabled]="!canSaveEdit || saving" (click)="saveEdit()">
                <i class="ph-bold ph-circle-notch icon-16 gable-spin" *ngIf="saving" aria-hidden="true"></i>
                <span>Save changes</span>
            </button>
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- ADD — upload a file                                           -->
    <!-- ============================================================ -->
    <div class="skill-add-upload" *ngSwitchCase="'add-upload'">
        <button type="button" class="back-link" (click)="view = 'list'">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>Add a skill</span>
        </button>
        <h3>Upload a file</h3>

        <!-- The zone itself is the control (role/tabindex/keydown), so the
             "Browse file" affordance inside it is a styled span — a real button
             there would nest one interactive element inside another. -->
        <div class="upload-drop"
             [class.is-dragging]="uploadDragging"
             [class.has-error]="uploadError !== null"
             role="button"
             tabindex="0"
             aria-label="Choose a Markdown file, or drag and drop one here"
             (click)="triggerUpload()"
             (keydown.enter)="triggerUpload()"
             (keydown.space)="$event.preventDefault(); triggerUpload()"
             (dragover)="onUploadDragOver($event)"
             (dragleave)="onUploadDragLeave($event)"
             (drop)="onUploadDrop($event)">
            <span class="upload-drop-icon" aria-hidden="true">
                <i class="ph-bold ph-cloud-arrow-up" aria-hidden="true"></i>
            </span>
            <span class="upload-drop-title">Choose a file or drag &amp; drop it here</span>
            <span class="upload-drop-hint">
                Markdown (.md) only — Gable reads the front matter for the skill's name and description.
            </span>
            <span class="btn-sm btn-secondary upload-drop-btn">Browse file</span>
        </div>

        <p class="upload-error" *ngIf="uploadError as message" role="alert">
            <i class="ph-bold ph-warning-circle icon-16" aria-hidden="true"></i>
            <span>{{ message }}</span>
        </p>
    </div>

    <!-- ============================================================ -->
    <!-- ADD — write it with Gable                                     -->
    <!-- ============================================================ -->
    <div class="skill-add-gable" *ngSwitchCase="'add-gable'" [attr.aria-busy]="drafting ? 'true' : null">
        <button type="button" class="back-link" (click)="onLeaveDraft()">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>Add a skill</span>
        </button>
        <h3>Write it with Gable</h3>
        <p class="helper-text">
            Describe what you want in plain words — Gable will draft the name, description, and
            instructions for you to review.
        </p>
        <textarea
            class="text-field"
            rows="6"
            maxlength="2000"
            [(ngModel)]="briefText"
            [readonly]="drafting"
            [class.is-locked]="drafting"
            placeholder="e.g. When coding transactions, always check vendor history first…"></textarea>

        <!-- The draft round-trips through an LLM, so it runs for seconds, not
             milliseconds. A disabled button alone reads as a dead page: say
             what is happening, and say it in a live region so a screen reader
             hears it too. -->
        <p class="draft-status" *ngIf="drafting" role="status" aria-live="polite">
            <i class="ph-bold ph-circle-notch icon-16 gable-spin" aria-hidden="true"></i>
            <span>Gable is drafting your skill — this usually takes a few seconds.</span>
        </p>

        <div class="edit-actions">
            <button type="button" class="btn-sm btn-secondary" (click)="onLeaveDraft()">Cancel</button>
            <button type="button" class="btn-sm btn-primary" [disabled]="!briefText.trim() || drafting" (click)="onDraftWithGable()">
                <i class="ph-bold ph-circle-notch icon-16 gable-spin" *ngIf="drafting" aria-hidden="true"></i>
                <span>{{ drafting ? 'Drafting…' : 'Draft it' }}</span>
            </button>
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- ADD — browse the PayHOA library                               -->
    <!-- ============================================================ -->
    <div class="skill-add-library" *ngSwitchCase="'add-library'">
        <button type="button" class="back-link" (click)="view = 'list'">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>Add a skill</span>
        </button>
        <h3>The PayHOA library</h3>
        <div class="starters-skeleton" *ngIf="startersLoading" role="status" aria-live="polite" aria-busy="true">
            <span class="visually-hidden">Loading the PayHOA library</span>
            <div class="starter-row starters-skeleton-row" *ngFor="let row of skeletonCards3; trackBy: trackByIndex" aria-hidden="true">
                <div class="starter-text">
                    <span class="gable-skeleton-line gable-skeleton-line--sm"></span>
                    <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                </div>
                <span class="starters-skeleton-btn"></span>
            </div>
        </div>
        <div class="starter-row" *ngFor="let s of starters; trackBy: trackByStarterKey">
            <div class="starter-text">
                <span class="starter-name">{{ s.name }}</span>
                <span class="starter-desc">{{ s.description }}</span>
            </div>
            <button type="button" class="btn-sm btn-secondary" (click)="addStarter(s)">Add</button>
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- REACH EDITOR                                                  -->
    <!-- ============================================================ -->
    <div class="reach-editor" *ngSwitchCase="'reach'">
        <button type="button" class="back-link" (click)="cancelReach()">
            <i class="ph-bold ph-arrow-left icon-16" aria-hidden="true"></i>
            <span>Back</span>
        </button>
        <h3>Who does this apply to?</h3>

        <div class="mode-toggle">
            <button type="button" class="mode-btn" [class.is-active]="reach.mode === 'all'" (click)="setReachMode('all')">
                All organizations
            </button>
            <button type="button" class="mode-btn" [class.is-active]="reach.mode === 'selected'" (click)="setReachMode('selected')">
                Selected organizations
            </button>
        </div>

        <ng-container *ngIf="reach.mode === 'selected'">
            <div class="reach-section">
                <p class="reach-question">Where are they?</p>
                <p class="reach-helper">All 50 states. Narrow it down only if the skill is state-specific.</p>

                <div class="state-pills" *ngIf="reach.filters.states.length > 0">
                    <span class="state-pill" *ngFor="let st of reach.filters.states; trackBy: trackByState">
                        {{ stateLabel(st) }}
                        <button type="button" (click)="removeState(st)" aria-label="Remove state">
                            <i class="ph-bold ph-x icon-12" aria-hidden="true"></i>
                        </button>
                    </span>
                </div>

                <button type="button" class="fchip" *ngIf="!statesExpanded" (click)="statesExpanded = true">
                    <i class="ph-bold ph-plus icon-12" aria-hidden="true"></i>
                    <span>Choose states</span>
                </button>

                <div class="state-picker" *ngIf="statesExpanded">
                    <input
                        type="text"
                        class="text-field"
                        placeholder="Search states"
                        [(ngModel)]="stateSearch"
                        aria-label="Search states">
                    <div class="state-checklist">
                        <label class="state-check-row" *ngFor="let st of filteredStates; trackBy: trackByState">
                            <input type="checkbox" [checked]="isStateSelected(st.value)" (change)="toggleState(st.value)">
                            <span>{{ st.label }}</span>
                        </label>
                    </div>
                    <button type="button" class="link-btn" (click)="statesExpanded = false">Done</button>
                </div>
            </div>

            <div class="reach-section">
                <p class="reach-question">Which communities?</p>
                <div class="size-chips">
                    <button type="button" class="fchip" [class.on]="isEveryOrgBand()" (click)="setSizeAll()">
                        <span>Every organization</span>
                        <span class="chip-count" *ngIf="bandCounts['all'] !== undefined">{{ bandCounts['all'] }}</span>
                    </button>
                    <button type="button" class="fchip" [class.on]="hasSizeBand('under_100')" (click)="toggleSizeBand('under_100')">
                        <span>Under 100 units</span>
                        <span class="chip-count" *ngIf="bandCounts['under_100'] !== undefined">{{ bandCounts['under_100'] }}</span>
                    </button>
                    <button type="button" class="fchip" [class.on]="hasSizeBand('100_500')" (click)="toggleSizeBand('100_500')">
                        <span>100–500 units</span>
                        <span class="chip-count" *ngIf="bandCounts['100_500'] !== undefined">{{ bandCounts['100_500'] }}</span>
                    </button>
                    <button type="button" class="fchip" [class.on]="hasSizeBand('over_500')" (click)="toggleSizeBand('over_500')">
                        <span>Over 500 units</span>
                        <span class="chip-count" *ngIf="bandCounts['over_500'] !== undefined">{{ bandCounts['over_500'] }}</span>
                    </button>
                    <button type="button" class="fchip" [class.on]="reach.filters.customRange !== null" (click)="setSizeCustom()">
                        <span>Custom range</span>
                    </button>
                </div>
                <div class="custom-range-inputs" *ngIf="reach.filters.customRange as range">
                    <input type="number" class="text-field" min="0" [(ngModel)]="range.min" (ngModelChange)="onReachChanged()" aria-label="Minimum units">
                    <span>to</span>
                    <input type="number" class="text-field" min="0" [(ngModel)]="range.max" (ngModelChange)="onReachChanged()" aria-label="Maximum units">
                    <span>units</span>
                </div>
            </div>

            <div class="reach-summary-bar" [class.zero]="!reachPreviewLoading && reachPreview?.count === 0">
                <span *ngIf="reachPreviewLoading">Calculating…</span>
                <ng-container *ngIf="!reachPreviewLoading && reachPreview as preview">
                    <strong>{{ preview.count }} of {{ preview.total }} organizations</strong>
                    <span *ngIf="preview.examples.length > 0"> — {{ preview.examples.join(', ') }}</span>
                </ng-container>
            </div>
            <div class="reach-zero-warning" *ngIf="!reachPreviewLoading && reachPreview?.count === 0">
                <i class="ph-bold ph-warning icon-16" aria-hidden="true"></i>
                <span>No organizations currently match these filters.</span>
                <label class="confirm-zero">
                    <input type="checkbox" [(ngModel)]="reachConfirmZero">
                    <span>Save anyway</span>
                </label>
            </div>

            <div class="reach-section">
                <p class="reach-question">Exceptions</p>
                <p class="reach-helper">
                    Exceptions stay put as organizations come and go. Everything else follows the filters,
                    so new communities that match are covered automatically.
                </p>
                <div class="exception-search">
                    <i class="ph-bold ph-magnifying-glass icon-muted icon-16" aria-hidden="true"></i>
                    <input
                        type="text"
                        placeholder="Search organizations"
                        [(ngModel)]="exceptionSearch"
                        (ngModelChange)="onExceptionSearchChange($event)"
                        aria-label="Search organizations for an exception">
                </div>
                <div class="exception-results" *ngIf="exceptionResults.length > 0">
                    <div class="exception-result-row" *ngFor="let org of exceptionResults; trackBy: trackByOrgLookup">
                        <span>{{ org.name }}</span>
                        <button type="button" class="btn-sm btn-secondary" *ngIf="org.matchesFilters" (click)="addException(org, 'never')">Exclude</button>
                        <button type="button" class="btn-sm btn-secondary" *ngIf="!org.matchesFilters" (click)="addException(org, 'always')">Include</button>
                    </div>
                </div>
                <div class="exception-tags" *ngIf="reach.exceptions.length > 0">
                    <span
                        class="exception-tag"
                        [class.always]="ex.type === 'always'"
                        [class.never]="ex.type === 'never'"
                        *ngFor="let ex of reach.exceptions; trackBy: trackByException">
                        <span class="exception-type">{{ ex.type === 'always' ? 'Always' : 'Never' }}</span>
                        <span>{{ ex.name }}</span>
                        <button type="button" (click)="removeException(ex)" aria-label="Remove exception">
                            <i class="ph-bold ph-x icon-12" aria-hidden="true"></i>
                        </button>
                    </span>
                </div>
            </div>
        </ng-container>

        <div class="edit-actions">
            <button type="button" class="btn-sm btn-secondary" (click)="cancelReach()">Cancel</button>
            <button type="button" class="btn-sm btn-primary" [disabled]="!canSaveReach" (click)="saveReach()">Save</button>
        </div>
    </div>

    <input #uploadInput type="file" accept=".md" class="visually-hidden" (change)="onUploadFileSelected($event)">
    <input #replaceInput type="file" accept=".md" class="visually-hidden" (change)="onReplaceFileSelected($event)">
</div>
